import { __extends, __spreadArray, __assign } from 'tslib';
import React__default from 'react';
import clsx from 'clsx';
import { IconCircleX } from '@tabler/icons';
import Label from '../Label/Label.component.js';
import Tooltip from '../Tooltip/Tooltip.component.js';
import { Variants } from '../Label/Label.types.js';
import ButtonBase from '../ButtonBase/ButtonBase.component.js';
import { propTypes, defaultProps } from './TagsInput.types.js';
import styles from './TagsInput.module.scss.js';

var TagsInput = /** @class */ (function (_super) {
    __extends(TagsInput, _super);
    function TagsInput() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            input: '',
        };
        /** Handler to add tags */
        _this.addTag = function (tag) {
            var _a = _this.props, onTagAdd = _a.onTagAdd, useBadges = _a.useBadges, tagList = _a.tagList;
            if (!useBadges) {
                onTagAdd(tag);
            }
            else {
                // check to avoid having duplicate tags
                if (!(tagList === null || tagList === void 0 ? void 0 : tagList.includes(tag))) {
                    onTagAdd(tag);
                }
            }
            // clean input
            _this.setState({ input: '' });
        };
        /** Handle to remove tags by index */
        _this.removeTag = function (tag) {
            var _a = _this.props, tagList = _a.tagList, onTagRemove = _a.onTagRemove;
            var index = tagList
                ? tagList.findIndex(function (item) { return item === tag; })
                : -1;
            if (index > -1) {
                onTagRemove === null || onTagRemove === void 0 ? void 0 : onTagRemove(index);
            }
        };
        /**
         * Handle current user input, report new tags if `,` found
         * This function also handles copy-paste feature 'this,is,an,example'
         */
        _this.handleInput = function (input) {
            var newTags = input.includes(',') ? input.split(',') : [];
            if (newTags.length) {
                // Clean tags
                newTags = newTags
                    .map(function (t) { return t.trim(); })
                    .filter(function (t) { return !!t; });
                // Get rid of duplicates
                var uniqueTags = Array.from(new Set(__spreadArray([], newTags, true)));
                uniqueTags.forEach(function (tag) {
                    _this.addTag(tag);
                });
            }
            else {
                _this.setState({ input: input });
            }
        };
        /** When (X) button is clicked clean input value and invoke onClear if passed **/
        _this.handleClearInput = function () {
            var onClear = _this.props.onClear;
            _this.setState({ input: '' });
            onClear === null || onClear === void 0 ? void 0 : onClear();
        };
        /**
         * User friendly behavior when keys are pressed on focused input:
         * - BACKSPACE: If no input and tags exist, remove most recent tag
         * - ENTER: If there's input push it to tag list and clean input
         * @param event: onKeyUp / onKeyPress from <input> element.
         */
        _this.handleInputKeyEvents = function (event) {
            var _a = _this.props, tagList = _a.tagList, onTagRemove = _a.onTagRemove;
            var input = _this.state.input;
            var pressedKey = event.key;
            if (pressedKey === 'Backspace' && !input && (tagList === null || tagList === void 0 ? void 0 : tagList.length)) {
                onTagRemove === null || onTagRemove === void 0 ? void 0 : onTagRemove(tagList.length - 1);
            }
            else if (pressedKey === 'Enter') {
                // Default behavior removes tag
                event.preventDefault();
                _this.handleInput(input + ',');
            }
        };
        return _this;
    }
    TagsInput.prototype.componentDidMount = function () {
        if (this.props.testId) {
            console.warn('DEPRECATION WARNING: The `testId` prop is deprecated and support for it will be removed soon. Please use `data-testid` instead.');
        }
    };
    /** Helper renderer for the tag badge **/
    TagsInput.prototype.renderBadge = function (name, key) {
        var _this = this;
        var withTooltips = this.props.withTooltips;
        return (React__default.createElement(Label, { key: key, text: name, variant: Variants.BASE, tooltipProps: { disabled: withTooltips }, className: clsx(styles.Tags, withTooltips && styles.TagWithTooltip), "data-testid": this.props.badgeTestId, suffixIcon: 
            /*
                A temporary solution to reduce the bundle,
                after fixing the Icon component,
                uncomment IconButton and remove inline svg with ButtonBase
            */
            React__default.createElement(ButtonBase, { onClick: function () { return _this.removeTag(name); }, className: styles.Tags_clearIcon, type: "button", "data-testid": 'clear-button', title: "remove tag: " + name },
                React__default.createElement(IconCircleX, { size: 18 })) }));
    };
    /** Helper renderer for tag list **/
    TagsInput.prototype.renderTags = function (tagList) {
        var _this = this;
        var _a = this.props, withTooltips = _a.withTooltips, portalTarget = _a.portalTarget, tooltipClassName = _a.tooltipClassName;
        return tagList.map(function (name) {
            return withTooltips ? (React__default.createElement(Tooltip, { key: name, tooltipContent: name, variant: "top", className: styles.Tooltip, portalTarget: portalTarget, tooltipClassName: tooltipClassName }, _this.renderBadge(name))) : (_this.renderBadge(name, name));
        });
    };
    TagsInput.prototype.render = function () {
        var _this = this;
        var _a = this.props, label = _a.label, className = _a.className, placeholder = _a.placeholder, testId = _a.testId, isValid = _a.isValid, name = _a.name, primaryCondition = _a.primaryCondition, secondaryCondition = _a.secondaryCondition, clearable = _a.clearable;
        var tagList = this.props.tagList || [];
        var input = this.state.input;
        var empty = input.length === 0 && tagList.length === 0;
        return (React__default.createElement("div", { className: clsx(styles.TagsInput, className) },
            React__default.createElement("div", { className: styles.TagsInput_titleWrapper }, (label || primaryCondition) && (React__default.createElement("div", { className: styles.TitleWrapper },
                React__default.createElement("label", { htmlFor: name, className: styles.TitleWrapper_label }, label),
                React__default.createElement("div", { className: styles.TitleWrapper_primaryCondition }, primaryCondition)))),
            React__default.createElement("div", { className: clsx(styles.TagsInput_wrapper, !isValid && styles.TagsInput_wrapper___invalid) },
                React__default.createElement("div", { className: styles.TagsWrapper },
                    tagList.length ? this.renderTags(tagList) : '',
                    React__default.createElement("input", { autoComplete: "off", name: name, value: input, onChange: function (e) {
                            return _this.handleInput(e.target.value || '');
                        }, onKeyDown: this.handleInputKeyEvents, onKeyPress: this.handleInputKeyEvents, className: clsx(styles.TagsWrapper_input, clearable &&
                            styles.TagsWrapper_input___clearable, empty && styles.TagsWrapper_input___empty), "data-testid": this.props['data-testid'] || testId, placeholder: tagList.length ? undefined : placeholder }),
                    clearable && (React__default.createElement(ButtonBase, { onClick: this.handleClearInput, className: clsx(styles.TagsWrapper_closeButton, styles.TagsWrapper_closeButton___borderless), type: "button", tabIndex: -1, "data-testid": 'clear-input', title: 'clear input' },
                        React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: 18, height: 18, viewBox: "0 0 24 24", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" },
                            React__default.createElement("path", { fill: "none", d: "M0 0h24v24H0z" }),
                            React__default.createElement("circle", { fill: "#BBC0CE", cx: "12", cy: "12", r: "9" }),
                            React__default.createElement("path", { stroke: "#FFFFFF", d: "M10 10l4 4m0 -4l-4 4" })))
                    //        <IconButton
                    //        disableBorder
                    //        name="ClearSolidIcon"
                    //        size={18}
                    //        alt="clear input"
                    //        onClick={this.handleClearInput}
                    //        buttonProps={{
                    //            tabIndex: -1,
                    //            type: 'button',
                    //        }}
                    //        className={styles.TagsWrapper_closeButton}
                    //    />
                    ))),
            secondaryCondition && (React__default.createElement("div", { className: clsx(styles.TagsInput_secondaryCondition, !isValid &&
                    styles.TagsInput_secondaryCondition___invalid) }, secondaryCondition))));
    };
    TagsInput.displayName = 'TagsInput';
    TagsInput.propTypes = propTypes;
    TagsInput.defaultProps = __assign({}, defaultProps);
    return TagsInput;
}(React__default.Component));

export { TagsInput as default };
